/// <amd-module name="Wit/DeliveryPlans/PlanSettingsPanel/Components/DeliveryPlansFieldSettingsView" />
import * as React from "react";
import { IObservableValue, useObservable } from "VSS/Core/Observable";
import { CardFieldSettings } from "Wit/Clients/Work/Contracts/Work";
import { FieldSettingsView, validateAdditionalFields } from "Wit/Common/WitSettingsViews/Components/FieldSettingsView";
import { IFieldSettings, IFieldSettingsViewProps } from "Wit/Common/WitSettingsViewsTypes/Components/FieldSettingsView.props";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { DeliveryPlansExcludedAdditionalFields, SettingsViewConstants } from "../PlanSettingsConstants";

export interface DeliveryPlansFieldSettingsViewProps extends Pick<IFieldSettingsViewProps, "disabled" | "labels">, IFieldSettings {
    originalAdditionalFields: string[];
    originalCardFieldSettings: CardFieldSettings;
    isDirty: IObservableValue<boolean>;
    isValid: IObservableValue<boolean>;
}

export const DeliveryPlansFieldSettingsView: React.FunctionComponent<DeliveryPlansFieldSettingsViewProps> = ({
    isDirty,
    isValid,
    originalAdditionalFields,
    originalCardFieldSettings,
    ...props
}) => {
    const [errorMessage] = useObservable<string | undefined>(undefined);
    return (
        <FieldSettingsView
            className="delivery-plans-settings-table"
            description={Resources.FieldTabDescription}
            maxAdditionalFields={SettingsViewConstants.maxAdditionalFields}
            errorMessage={errorMessage}
            excludedFieldReferenceNames={DeliveryPlansExcludedAdditionalFields}
            onSettingsChanged={({ additionalFields, coreFields }) => {
                let checkboxesDirty = false;
                forEachSetting(originalCardFieldSettings, (key, value) => {
                    checkboxesDirty = checkboxesDirty || coreFields[key].value !== value;
                });

                const overallDirty =
                    checkboxesDirty ||
                    areAdditionalFieldsDirty(
                        additionalFields.value.map(field => field.referenceName),
                        originalAdditionalFields
                    );

                if (overallDirty !== isDirty.value) {
                    isDirty.value = overallDirty;
                }

                const updatedErrorMessage = validateAdditionalFields(additionalFields.value);
                const updatedIsValid = updatedErrorMessage === undefined;

                if (updatedErrorMessage !== errorMessage.value) {
                    errorMessage.value = updatedErrorMessage;
                }

                if (updatedIsValid !== isValid.value) {
                    isValid.value = updatedIsValid;
                }
            }}
            {...props}
        />
    );
};

export function forEachSetting(cardFieldSettings: CardFieldSettings, func: (key: string, value: boolean) => void) {
    for (const [settingKey, settingValue] of Object.entries(cardFieldSettings)) {
        if (typeof settingValue === "boolean") {
            func(settingKey, settingValue);
        }
    }
}

function areAdditionalFieldsDirty(currentFields: string[], originalFields: string[]) {
    if (currentFields.length !== originalFields.length) {
        return true;
    }

    return currentFields.findIndex((fieldRefName, index) => fieldRefName !== originalFields[index]) >= 0;
}
